/**
 * Notification Service
 * Handles sending SMS notifications via mnotify API
 * Documentation: https://mnotify.com/developers
 */

import { supabase } from '@/lib/supabase'

interface MNotifyConfig {
  apiKey: string
  senderId: string // Your sender ID (e.g., "ChurchName")
}

interface NotificationPreferences {
  contribution_notifications: boolean
  event_reminders: boolean
  announcement_alerts: boolean
}

export class NotificationService {
  private static readonly MNOTIFY_API_URL = 'https://api.mnotify.com/api/sms/quick'

  /**
   * Get church's mnotify configuration from database
   */
  private static async getMNotifyConfig(churchId: string): Promise<MNotifyConfig | null> {
    try {
      const { data, error } = await supabase
        .from('church_settings')
        .select('mnotify_api_key, mnotify_sender_id')
        .eq('church_id', churchId)
        .single()

      if (error || !data?.mnotify_api_key) {
        console.warn('mnotify not configured for church')
        return null
      }

      return {
        apiKey: data.mnotify_api_key,
        senderId: data.mnotify_sender_id || 'Church'
      }
    } catch (error) {
      console.error('Error fetching mnotify config:', error)
      return null
    }
  }

  /**
   * Get member's notification preferences
   */
  private static async getNotificationPreferences(
    memberId: string
  ): Promise<NotificationPreferences> {
    try {
      const { data, error } = await supabase
        .from('member_preferences')
        .select('contribution_notifications, event_reminders, announcement_alerts')
        .eq('member_id', memberId)
        .single()

      if (error || !data) {
        // Default: all notifications enabled
        return {
          contribution_notifications: true,
          event_reminders: true,
          announcement_alerts: true
        }
      }

      return data
    } catch (error) {
      console.error('Error fetching notification preferences:', error)
      return {
        contribution_notifications: true,
        event_reminders: true,
        announcement_alerts: true
      }
    }
  }

  /**
   * Send SMS via mnotify API
   */
  private static async sendSMS(
    config: MNotifyConfig,
    recipient: string,
    message: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Format phone number (remove spaces, ensure it starts with country code)
      const formattedPhone = recipient.replace(/\s+/g, '').replace(/^0/, '233')

      const response = await fetch(this.MNOTIFY_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.apiKey}`
        },
        body: JSON.stringify({
          recipient: [formattedPhone],
          sender: config.senderId,
          message: message,
          is_schedule: false
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          success: false,
          error: errorData.message || `HTTP ${response.status}`
        }
      }

      const data = await response.json()
      return { success: true }
    } catch (error: any) {
      console.error('Error sending SMS:', error)
      return {
        success: false,
        error: error.message || 'Failed to send SMS'
      }
    }
  }

  /**
   * Log notification in database for audit trail
   */
  private static async logNotification(
    churchId: string,
    memberId: string,
    notificationType: string,
    message: string,
    status: 'sent' | 'failed',
    errorMessage?: string
  ): Promise<void> {
    try {
      await supabase.from('notification_logs').insert([{
        church_id: churchId,
        member_id: memberId,
        notification_type: notificationType,
        message: message,
        status: status,
        error_message: errorMessage,
        sent_at: new Date().toISOString()
      }])
    } catch (error) {
      console.error('Error logging notification:', error)
    }
  }

  /**
   * Send contribution notification to member
   */
  static async notifyContributionRecorded(
    churchId: string,
    memberId: string,
    contribution: {
      amount: number
      currency: string
      contribution_type: string
      contribution_date: string
      reference_number?: string
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Check if member wants contribution notifications
      const preferences = await this.getNotificationPreferences(memberId)
      if (!preferences.contribution_notifications) {
        console.log('Member has disabled contribution notifications')
        return { success: true } // Not an error, just disabled
      }

      // Get member phone number
      const { data: member, error: memberError } = await supabase
        .from('members')
        .select('phone_number, preferred_name')
        .eq('id', memberId)
        .single()

      if (memberError || !member?.phone_number) {
        console.warn('Member has no phone number')
        return { success: false, error: 'No phone number available' }
      }

      // Get mnotify configuration
      const config = await this.getMNotifyConfig(churchId)
      if (!config) {
        return { success: false, error: 'mnotify not configured' }
      }

      // Format contribution type for display
      const typeDisplay = contribution.contribution_type
        .replace('_', ' ')
        .replace(/\b\w/g, l => l.toUpperCase())

      // Compose message
      const message = `Dear ${member.preferred_name},\n\n` +
        `Thank you! We've recorded your ${typeDisplay} of ` +
        `${contribution.currency} ${contribution.amount.toLocaleString()} ` +
        `dated ${new Date(contribution.contribution_date).toLocaleDateString()}.\n\n` +
        (contribution.reference_number ? `Ref: ${contribution.reference_number}\n\n` : '') +
        `God bless you!`

      // Send SMS
      const result = await this.sendSMS(config, member.phone_number, message)

      // Log the notification
      await this.logNotification(
        churchId,
        memberId,
        'contribution_recorded',
        message,
        result.success ? 'sent' : 'failed',
        result.error
      )

      return result
    } catch (error: any) {
      console.error('Error in notifyContributionRecorded:', error)
      return {
        success: false,
        error: error.message || 'Notification failed'
      }
    }
  }

  /**
   * Send event reminder notification
   */
  static async notifyEventReminder(
    churchId: string,
    memberId: string,
    event: {
      title: string
      start_time: string
      location?: string
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const preferences = await this.getNotificationPreferences(memberId)
      if (!preferences.event_reminders) {
        return { success: true }
      }

      const { data: member } = await supabase
        .from('members')
        .select('phone_number, preferred_name')
        .eq('id', memberId)
        .single()

      if (!member?.phone_number) {
        return { success: false, error: 'No phone number' }
      }

      const config = await this.getMNotifyConfig(churchId)
      if (!config) {
        return { success: false, error: 'mnotify not configured' }
      }

      const eventDate = new Date(event.start_time)
      const message = `Hi ${member.preferred_name},\n\n` +
        `Reminder: ${event.title}\n` +
        `Date: ${eventDate.toLocaleDateString()}\n` +
        `Time: ${eventDate.toLocaleTimeString()}\n` +
        (event.location ? `Location: ${event.location}\n` : '') +
        `\nSee you there!`

      const result = await this.sendSMS(config, member.phone_number, message)

      await this.logNotification(
        churchId,
        memberId,
        'event_reminder',
        message,
        result.success ? 'sent' : 'failed',
        result.error
      )

      return result
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  }

  /**
   * Send bulk announcement to multiple members
   */
  static async sendBulkAnnouncement(
    churchId: string,
    memberIds: string[],
    announcement: string
  ): Promise<{ sent: number; failed: number }> {
    const config = await this.getMNotifyConfig(churchId)
    if (!config) {
      return { sent: 0, failed: memberIds.length }
    }

    let sent = 0
    let failed = 0

    for (const memberId of memberIds) {
      try {
        const preferences = await this.getNotificationPreferences(memberId)
        if (!preferences.announcement_alerts) {
          continue
        }

        const { data: member } = await supabase
          .from('members')
          .select('phone_number')
          .eq('id', memberId)
          .single()

        if (!member?.phone_number) {
          failed++
          continue
        }

        const result = await this.sendSMS(config, member.phone_number, announcement)

        await this.logNotification(
          churchId,
          memberId,
          'announcement',
          announcement,
          result.success ? 'sent' : 'failed',
          result.error
        )

        if (result.success) {
          sent++
        } else {
          failed++
        }

        // Rate limiting: wait 100ms between messages
        await new Promise(resolve => setTimeout(resolve, 100))
      } catch (error) {
        console.error(`Error sending to member ${memberId}:`, error)
        failed++
      }
    }

    return { sent, failed }
  }
}

export default NotificationService
